import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Loader2, Play, Youtube } from 'lucide-react'
import { youtube } from '../api/client'
import { useVideoPlayerStore } from '../stores/videoPlayer'

function formatDuration(seconds: number | null) {
  if (!seconds || !Number.isFinite(seconds)) return '0:00'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remaining = Math.floor(seconds % 60)
  return hours > 0
    ? `${hours}:${minutes.toString().padStart(2, '0')}:${remaining.toString().padStart(2, '0')}`
    : `${minutes}:${remaining.toString().padStart(2, '0')}`
}

export default function YoutubeWatch() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const playVideo = useVideoPlayerStore((state) => state.playVideo)
  const currentVideo = useVideoPlayerStore((state) => state.currentVideo)
  const isCurrentVideo = currentVideo?.mediaType === 'youtube' && currentVideo.id === id

  const { data: video, isLoading, error } = useQuery({
    queryKey: ['youtube', 'video', id],
    queryFn: () => youtube.getVideo(id),
    enabled: id.length > 0,
  })

  useEffect(() => {
    if (!video || !id) return
    if (isCurrentVideo) return
    playVideo({
      id,
      title: video.title,
      seriesTitle: video.channel,
      thumbnailPath: video.thumbnail || undefined,
      durationMs: video.duration_seconds ? video.duration_seconds * 1000 : undefined,
      streamUrl: youtube.streamUrl(id),
      mediaType: 'youtube',
    })
  }, [video, id, isCurrentVideo, playVideo])

  if (isLoading) {
    return <div className="grid min-h-full place-items-center bg-dark-900"><Loader2 className="animate-spin text-primary-400" size={34} /></div>
  }

  if (error || !video) {
    return (
      <div className="min-h-full bg-dark-900 p-4 text-dark-100 sm:p-6">
        <Link to="/youtube" className="inline-flex items-center gap-2 text-dark-400 hover:text-white"><ArrowLeft size={18} /> Back to YouTube</Link>
        <div className="mt-8 rounded-2xl border border-red-500/30 bg-red-500/10 p-5 text-red-200">Unable to resolve this video.</div>
      </div>
    )
  }

  return (
    <div className="min-h-full overflow-x-hidden bg-dark-900 text-dark-100">
      <div className="mx-auto w-full max-w-5xl px-4 py-5 pb-32 sm:px-6 lg:px-8">
        <button onClick={() => navigate('/youtube')} className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm text-dark-400 transition hover:bg-dark-800 hover:text-white">
          <ArrowLeft size={18} /> Back to YouTube
        </button>

        <div className="overflow-hidden rounded-3xl border border-dark-700 bg-dark-950 shadow-2xl shadow-black/30">
          <button
            onClick={() => playVideo({
              id,
              title: video.title,
              seriesTitle: video.channel,
              thumbnailPath: video.thumbnail || undefined,
              durationMs: video.duration_seconds ? video.duration_seconds * 1000 : undefined,
              streamUrl: youtube.streamUrl(id),
              mediaType: 'youtube',
            })}
            className="group relative block aspect-video w-full overflow-hidden bg-black text-left"
          >
            {video.thumbnail && <img src={video.thumbnail} alt="" className="h-full w-full object-cover opacity-80 transition group-hover:scale-[1.02] group-hover:opacity-95" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute inset-0 grid place-items-center">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white text-dark-950 shadow-xl transition group-active:scale-95">
                <Play size={34} fill="currentColor" className="ml-1" />
              </span>
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-sm font-bold text-white backdrop-blur"><Youtube size={17} /> YouTube</span>
              <span className="rounded-full bg-black/55 px-3 py-1.5 text-sm text-white backdrop-blur">{formatDuration(video.duration_seconds)}</span>
            </div>
          </button>
        </div>

        <article className="mt-7 rounded-3xl border border-dark-700 bg-dark-850/70 p-5 sm:p-6">
          <h1 className="break-words text-2xl font-black leading-tight text-white sm:text-3xl">{video.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-dark-400">
            <span className="font-semibold text-primary-400">{video.channel}</span>
            {video.duration_seconds && <span>{formatDuration(video.duration_seconds)}</span>}
            <a href={video.webpage_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-dark-300 hover:text-white">
              Open on YouTube <ExternalLink size={14} />
            </a>
          </div>
          {video.description && (
            <p className="mt-5 max-h-80 overflow-y-auto whitespace-pre-line rounded-2xl bg-dark-950/70 p-4 leading-7 text-dark-300">
              {video.description}
            </p>
          )}
        </article>

        <p className="mt-5 text-center text-sm text-dark-500">Playback is in the bottom bar. Tap the video thumbnail in the bar to open the full player.</p>
      </div>
    </div>
  )
}
